import React, { useEffect, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { portalBase } from '../lib/api';
import { InteractionError, allow, loadStep, refuse, sendCode, verifyCode, type InteractionStep } from '../lib/oauthInteraction';
import { usePageMeta } from '../hooks/usePageMeta';

/**
 * Where an app connecting to Pawsitive Future (Claude, ChatGPT, Codex …) sends the person: sign in
 * with an emailed code, then allow or refuse. The authorization server owns every decision; this
 * page shows the step it answers with and, once a step finishes, follows the URL it hands back.
 */
type View =
  | { kind: 'loading' }
  | { kind: 'sign-in'; client: string }
  | { kind: 'code'; email: string; client: string }
  | { kind: 'consent'; client: string; email: string }
  | { kind: 'leaving' }
  | { kind: 'ended'; message: string };

const INPUT_CLASS = 'w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50';
const PRIMARY_CLASS = 'btn-primary w-full px-6 py-3 rounded-full font-medium disabled:opacity-50 disabled:cursor-not-allowed';
const SECONDARY_CLASS = 'w-full px-6 py-3 rounded-full font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50';

const OAuthInteractionPage: React.FC = () => {
  const { uid = '' } = useParams();
  const [view, setView] = useState<View>({ kind: 'loading' });
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  usePageMeta('Connect an app · Pawsitive Future', 'Sign in to connect an app to your Pawsitive Future account.', '/oauth/interaction');

  /** Show what the server answered, or follow it out to the app. */
  const show = (next: InteractionStep, client = ''): void => {
    if ('redirectTo' in next) {
      setView({ kind: 'leaving' });
      window.location.assign(next.redirectTo);
    } else if (next.step === 'code') {
      setView({ kind: 'code', email: next.email, client });
      setNotice(`If ${next.email} has an account, a 6-digit code is on its way.`);
    } else {
      setView(next.step === 'sign-in' ? { kind: 'sign-in', client: next.client } : { kind: 'consent', client: next.client, email: next.email });
    }
  };

  const fail = (err: unknown): void => {
    if (err instanceof InteractionError && err.expired) setView({ kind: 'ended', message: err.message });
    else setError(err instanceof InteractionError ? err.message : 'Something went wrong. Please try again.');
  };

  const run = async (action: () => Promise<InteractionStep>, client = ''): Promise<void> => {
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      show(await action(), client);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    loadStep(uid).then((next) => show(next), fail);
  }, [uid]);

  const client = 'client' in view ? view.client : '';

  const submitEmail = (e: FormEvent): void => {
    e.preventDefault();
    void run(() => sendCode(uid, email.trim()), client);
  };

  const submitCode = (e: FormEvent): void => {
    e.preventDefault();
    if (view.kind === 'code') void run(() => verifyCode(uid, view.email, code.trim()), client);
  };

  return (
    <section className="py-20 px-6">
      <div className="mx-auto card p-8 max-w-md">
        {view.kind === 'loading' && <p className="text-center text-gray-500">Loading…</p>}

        {view.kind === 'leaving' && <p className="text-center text-gray-500" role="status">Returning you to the app…</p>}

        {view.kind === 'ended' && (
          <>
            <h1 className="text-2xl font-bold text-center">We couldn&apos;t finish that</h1>
            <p className="mt-4 text-center text-sm text-gray-500">{view.message}</p>
            <p className="mt-4 text-center text-xs text-gray-400">Close this window and connect again from the app you were using.</p>
          </>
        )}

        {view.kind === 'sign-in' && (
          <>
            <h1 className="text-2xl font-bold text-center">Sign in to continue</h1>
            <p className="mt-4 text-center text-sm text-gray-500">
              <strong className="text-gray-700">{view.client}</strong> is asking to connect to your Pawsitive Future account.
            </p>
            <form onSubmit={submitEmail} className="mt-6 space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email address</label>
                <input
                  id="email"
                  className={INPUT_CLASS}
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={busy}
                  required
                  autoFocus
                />
              </div>
              <button type="submit" className={PRIMARY_CLASS} disabled={busy || !email.trim()}>
                {busy ? 'Sending…' : 'Email me a code'}
              </button>
            </form>
            <p className="mt-4 text-center text-xs text-gray-400">We email a 6-digit code. There is no password to type.</p>
          </>
        )}

        {view.kind === 'code' && (
          <>
            <h1 className="text-2xl font-bold text-center">Check your email</h1>
            <form onSubmit={submitCode} className="mt-6 space-y-4">
              <div>
                <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1">6-digit code</label>
                <input
                  id="code"
                  className={`${INPUT_CLASS} text-center text-2xl tracking-[0.5em]`}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  disabled={busy}
                  required
                  autoFocus
                />
                <p className="mt-1 text-xs text-gray-400">Sent to {view.email}</p>
              </div>
              <button type="submit" className={PRIMARY_CLASS} disabled={busy || code.trim().length < 6}>
                {busy ? 'Signing in…' : 'Sign in'}
              </button>
              <button type="button" className={SECONDARY_CLASS} disabled={busy} onClick={() => { setCode(''); void run(() => sendCode(uid, view.email), client); }}>
                Send a new code
              </button>
            </form>
          </>
        )}

        {view.kind === 'consent' && (
          <>
            <h1 className="text-2xl font-bold text-center">Allow {view.client} to act as you?</h1>
            <p className="mt-4 text-center text-sm text-gray-500">
              Signed in as <strong className="text-gray-700">{view.email}</strong>.
            </p>
            <ul className="mt-6 space-y-2 rounded-xl bg-gray-50 px-5 py-4 text-sm text-gray-600 list-disc list-inside">
              <li>Reads and acts on Pawsitive Future with your own permissions — nothing more</li>
              <li>Every action it takes is recorded against your account</li>
              <li>Stays connected until you revoke it, or after 90 days without use</li>
            </ul>
            <div className="mt-6 space-y-3">
              <button type="button" className={PRIMARY_CLASS} disabled={busy} onClick={() => void run(() => allow(uid))}>
                {busy ? 'Connecting…' : 'Allow access'}
              </button>
              <button type="button" className={SECONDARY_CLASS} disabled={busy} onClick={() => void run(() => refuse(uid))}>
                Cancel
              </button>
            </div>
            <p className="mt-4 text-center text-xs text-gray-400">Only allow this if you started it from {view.client}.</p>
          </>
        )}

        {notice && <p className="mt-4 rounded-xl bg-green-50 px-4 py-3 text-sm text-green-700" role="status">{notice}</p>}
        {error && <p className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">{error}</p>}

        {(view.kind === 'sign-in' || view.kind === 'code') && (
          <p className="mt-6 text-center text-xs text-gray-400">
            By signing in you agree to the{' '}
            <a href={`${portalBase}/terms`} target="_blank" rel="noopener" className="text-primary hover:underline">Terms of Service</a> and the{' '}
            <a href={`${portalBase}/privacy`} target="_blank" rel="noopener" className="text-primary hover:underline">Privacy Policy</a>.
          </p>
        )}
      </div>
    </section>
  );
};

export default OAuthInteractionPage;
